/**
 * GroupBanner (inbox "Serien" plan §7). Rendered between `TransactionCard` and
 * `BucketGrid` whenever the current item belongs to a detected group of
 * related pending transactions (same payee, or the same PSP sub-merchant).
 * Surfaces the group summary (size, payee, amount range, cadence), a checkbox
 * ("Alle N zusammen zuordnen", hotkey `g`) that governs whether the grid's
 * tap/`Enter`/`1–9` actions apply to the whole group or just the current card,
 * an expandable member list, a session-only "Ignorieren", and "Als Regel
 * speichern" (opens the rule dialog prefilled from the group).
 */
import { useState } from 'react';
import { ChevronDown, ChevronRight, Repeat, X } from 'lucide-react';
import { AmountText } from '../../components/ui/AmountText.tsx';
import { Button } from '../../components/ui/Button.tsx';
import { formatDate, money } from '../../lib/format.ts';
import { GROUP_CADENCE, GROUP_CONFIDENCE } from '../../lib/labels.ts';
import type { GroupConfidence, InboxGroup } from '../../lib/types.ts';

const CONFIDENCE_STYLE: Record<GroupConfidence, string> = {
  high: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  medium: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
};

/** Drop the payee echo banks prefix the purpose with ("ROSSMANN … <ref>") so
 * member rows don't repeat the banner headline; returns '' when the purpose
 * is nothing but the echo. */
function stripPayeeEcho(purpose: string, payeeNames: string[]): string {
  for (const name of payeeNames) {
    if (name && purpose.toLowerCase().startsWith(name.toLowerCase())) {
      return purpose.slice(name.length).replace(/^[\s./,:-]+/, '');
    }
  }
  return purpose;
}

export interface GroupBannerProps {
  group: InboxGroup;
  applyEnabled: boolean;
  liveCount: number;
  onToggleApply: (enabled: boolean) => void;
  onIgnore: () => void;
  onSaveRule: () => void;
}

export function GroupBanner({
  group,
  applyEnabled,
  liveCount,
  onToggleApply,
  onIgnore,
  onSaveRule,
}: GroupBannerProps) {
  const [open, setOpen] = useState(false);

  // The account only differentiates members when the series spans several
  // accounts — a uniform account is already visible on the card above.
  const showAccounts =
    new Set(group.members.map((m) => m.account_name).filter(Boolean)).size > 1;
  const payeeNames = [group.display_name];
  if (group.sub_merchant) {
    payeeNames.push(group.display_name.replace(` (${group.sub_merchant})`, ''));
  }

  return (
    <div className="space-y-3 rounded-lg bg-sky-50 px-4 py-3 dark:bg-sky-950">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-2">
          <Repeat className="mt-0.5 h-4 w-4 shrink-0 text-sky-600 dark:text-sky-400" aria-hidden />
          <p className="min-w-0 text-sm text-sky-900 dark:text-sky-100">
            Serie erkannt: {group.size} Buchungen von{' '}
            <span className="font-semibold">{group.display_name}</span> ({money(group.amount_min)} bis{' '}
            {money(group.amount_max)})
            {group.cadence ? ` · ${GROUP_CADENCE[group.cadence]}` : null}
          </p>
        </div>
        <span
          className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${CONFIDENCE_STYLE[group.confidence]}`}
        >
          {GROUP_CONFIDENCE[group.confidence]}
        </span>
      </div>

      <label className="flex items-center gap-2 text-sm text-sky-900 dark:text-sky-100">
        <input
          type="checkbox"
          className="h-4 w-4 rounded border border-sky-200 accent-sky-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600 dark:border-sky-800 dark:accent-sky-500"
          checked={applyEnabled}
          onChange={(e) => onToggleApply(e.target.checked)}
        />
        Alle {liveCount} zusammen zuordnen
        <kbd className="rounded bg-white/50 px-1 text-[10px] text-sky-700 dark:bg-black/20 dark:text-sky-300">
          g
        </kbd>
      </label>

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 text-xs font-medium text-sky-700 transition-colors duration-150 hover:text-sky-900 dark:text-sky-300 dark:hover:text-sky-100"
        aria-expanded={open}
      >
        {open ? (
          <ChevronDown className="h-3.5 w-3.5 shrink-0" aria-hidden />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 shrink-0" aria-hidden />
        )}
        {open ? 'Buchungen ausblenden' : `${group.size} Buchungen anzeigen`}
      </button>

      {open ? (
        <ul className="max-h-72 space-y-1 overflow-y-auto border-t border-sky-100 pt-2 dark:border-sky-900">
          {group.members.map((member) => {
            const detail = [
              showAccounts ? member.account_name : null,
              member.purpose ? stripPayeeEcho(member.purpose, payeeNames) : null,
            ]
              .filter(Boolean)
              .join(' · ');
            const full = [member.account_name, member.purpose].filter(Boolean).join(' · ');
            return (
              <li key={member.id} className="flex items-center gap-3 text-sm">
                <span className="shrink-0 tabular-nums text-sky-800 dark:text-sky-200">
                  {formatDate(member.booking_date)}
                </span>
                <span
                  className="min-w-0 flex-1 truncate text-xs text-sky-700/80 dark:text-sky-400/80"
                  title={full || undefined}
                >
                  {detail}
                </span>
                <AmountText value={member.amount} className="shrink-0" />
              </li>
            );
          })}
        </ul>
      ) : null}

      <div className="flex items-center justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onIgnore}>
          <X className="h-4 w-4" aria-hidden />
          Ignorieren
        </Button>
        <Button variant="secondary" size="sm" onClick={onSaveRule}>
          Als Regel speichern
        </Button>
      </div>
    </div>
  );
}
